// src/Checklist/ChecklistFillModal.jsx
import React, { useEffect, useRef } from "react";
import { api } from "../api";
import "./ChecklistFillModal.css";

/*
  config = {
    mode: "self" | "assignment",
    title, htmlPath,
    templateId, projectId, projectName,   // used in "self" mode
    assignmentId, data, readOnly,         // used in "assignment" mode
    defaults,                             // e.g. { projectName, projectAddress, preparedBy }
    users,                                // array of user names for the dropdowns
  }

  For a saved checklist (assignmentId) the popup loads the record itself,
  so it always shows the latest data and the item comments.
*/
const ChecklistFillModal = ({ config, onClose, onSaved }) => {
  const iframeRef = useRef(null);
  const origin = window.location.origin;

  const authHeader = {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  };

  const sendToFrame = (msg) => {
    iframeRef.current?.contentWindow?.postMessage(msg, origin);
  };

  // Fill empty fields with defaults (never overwrites saved values,
  // and never touches a read-only / submitted view)
  const buildInitialData = (base) => {
    const merged = { ...(base || {}) };
    if (config.readOnly) return merged;

    const defaults = config.defaults || {};
    Object.keys(defaults).forEach((key) => {
      if (!merged[key] && defaults[key]) merged[key] = defaults[key];
    });
    return merged;
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    const handler = async (e) => {
      if (e.origin !== origin) return;
      if (e.source !== iframeRef.current?.contentWindow) return;

      const m = e.data || {};

      if (m.type === "checklist-ready") {
        // load the saved record (data + item comments)
        let record = null;
        if (config.assignmentId) {
          try {
            const res = await api.get(
              `/checklists/${config.assignmentId}`,
              authHeader,
            );
            record = res.data || null;
          } catch (err) {
            console.warn("Could not load the checklist record", err);
          }
        }

        sendToFrame({
          type: "checklist-init",
          data: buildInitialData(record?.data || config.data),
          defaults: config.defaults || {},
          users: config.users || [],
          readOnly: Boolean(config.readOnly),
          allowDraft: config.mode === "assignment",
          canComment: record ? record.status === "submitted" : false,
          comments: record?.itemComments || [],
        });
        return;
      }

      if (m.type === "checklist-comment") {
        try {
          const res = await api.post(
            `/checklists/${config.assignmentId}/comment`,
            {
              itemKey: m.itemKey,
              itemLabel: m.itemLabel,
              text: m.text,
            },
            authHeader,
          );

          sendToFrame({
            type: "checklist-comments",
            comments: res.data?.itemComments || [],
            message: "Comment sent.",
          });
        } catch (err) {
          sendToFrame({
            type: "checklist-result",
            ok: false,
            message:
              err?.response?.data?.message || "Could not send the comment.",
          });
        }
        return;
      }

      if (m.type === "checklist-save") {
        try {
          let res;
          if (config.mode === "self") {
            res = await api.post(
              "/checklists/fill-self",
              {
                templateId: config.templateId,
                projectId: config.projectId,
                data: m.data || {},
              },
              authHeader,
            );
          } else {
            res = await api.patch(
              `/checklists/${config.assignmentId}/save`,
              { data: m.data || {}, submit: Boolean(m.submit) },
              authHeader,
            );
          }

          sendToFrame({
            type: "checklist-result",
            ok: true,
            message: m.submit
              ? "Checklist submitted successfully."
              : "Draft saved.",
          });

          if (onSaved) onSaved(res.data, Boolean(m.submit));
          if (m.submit || config.mode === "self") setTimeout(onClose, 900);
        } catch (err) {
          sendToFrame({
            type: "checklist-result",
            ok: false,
            message:
              err?.response?.data?.message || "Could not save the checklist.",
          });
        }
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  return (
    <div className="cfm-overlay">
      <div className="cfm-box">
        <div className="cfm-header">
          <div className="cfm-title">
            {config.title}
            {config.projectName ? ` — ${config.projectName}` : ""}
          </div>
          <button type="button" className="cfm-close" onClick={onClose}>
            Close
          </button>
        </div>
        <iframe
          ref={iframeRef}
          src={config.htmlPath}
          title={config.title}
          className="cfm-frame"
        />
      </div>
    </div>
  );
};

export default ChecklistFillModal;
